import React from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import {
  useGiftCardQueue,
  useGiftCardActions,
  GiftCardStatus,
  GiftCardRedemption,
} from '../api/useGiftCardQueue';

const STATUSES: { id: GiftCardStatus; label: string }[] = [
  { id: 'pending_fulfillment', label: 'To fulfil' },
  { id: 'fulfilled', label: 'Fulfilled' },
  { id: 'refunded', label: 'Refunded' },
];

const dollars = (cents: number) => `$${(cents / 100).toFixed(0)}`;

/**
 * Gift card fulfilment queue.
 *
 * Cards are bought on demand, so this is a shopping list rather than an
 * inventory view: each row is the exact SKU to purchase and the address to send
 * it to, oldest first.
 *
 * Refund is deliberately as prominent as Fulfil. "That brand can't be sourced
 * right now" is an ordinary outcome when nothing is pre-purchased, not an
 * exception — burying it would make the common case the awkward one.
 */
export function GiftCardQueuePanel({ env }: { env: Env }) {
  const [status, setStatus] = React.useState<GiftCardStatus>('pending_fulfillment');
  const { data, loading, error, refetch } = useGiftCardQueue(env, status);
  const { fulfil, refund, busyId } = useGiftCardActions(env);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);

  const onFulfil = async (row: GiftCardRedemption) => {
    const code = window.prompt(
      `Paste the ${row.brand_name} code for ${row.delivery_email}.\n\n` +
        'It is emailed and never stored — it cannot be sent again.',
    );
    if (!code?.trim()) return;

    setNotice(null);
    setProblem(null);
    const res = await fulfil(row.id, code.trim());
    if (res.ok) {
      setNotice(`Sent to ${row.delivery_email}.`);
    } else if (res.sendFailed) {
      setProblem(`Not sent — still queued for retry. ${res.message}`);
    } else {
      setProblem(res.message);
    }
    refetch();
  };

  const onRefund = async (row: GiftCardRedemption) => {
    const reason = window.prompt(
      `Refund ${dollars(row.face_value_cents)} ${row.brand_name}?\n\n` +
        `${row.coins_cost.toLocaleString()} coins go back and the tier is restocked.\n\n` +
        'Reason:',
    );
    if (!reason?.trim()) return;

    setNotice(null);
    setProblem(null);
    const res = await refund(row.id, reason.trim());
    if (res.ok) {
      setNotice(`Refunded — ${row.coins_cost.toLocaleString()} coins returned, tier restocked.`);
    } else {
      setProblem(res.message);
    }
    refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${
                status === s.id
                  ? 'bg-violet-950/40 border-violet-500 text-violet-300'
                  : 'bg-gray-900 border-gray-800 text-gray-400 hover:text-gray-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          onClick={refetch}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border bg-gray-900 border-gray-800 text-gray-400 hover:text-gray-200"
        >
          Refresh
        </button>
      </div>

      {/* Remaining commitment per tier. Shared across every brand in the tier —
          a $25 Visa and a $25 Sephora draw on the same pool. */}
      {data?.tiers?.length ? (
        <div className="flex gap-3 flex-wrap">
          {data.tiers.map((t) => (
            <div
              key={t.id}
              className="px-4 py-2 rounded-xl border border-gray-800 bg-gray-900/60"
            >
              <div className="text-xs text-gray-500">
                {dollars(t.face_value_cents)} · {t.coins_cost.toLocaleString()} coins
              </div>
              <div
                className={`text-sm font-semibold ${
                  t.stock_remaining === 0 ? 'text-amber-400' : 'text-gray-200'
                }`}
              >
                {t.stock_remaining} left
                {!t.is_active && <span className="ml-2 text-xs text-gray-500">(inactive)</span>}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {notice && (
        <div className="px-4 py-2 rounded-lg border border-emerald-800 bg-emerald-950/40 text-emerald-300 text-sm">
          {notice}
        </div>
      )}
      {problem && <ErrorBanner message={problem} />}
      {error && <ErrorBanner message={error} />}

      {loading ? (
        <Spinner />
      ) : !data?.redemptions?.length ? (
        <div className="px-4 py-8 text-center text-sm text-gray-500 rounded-xl border border-gray-800 bg-gray-900/40">
          Nothing here.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-900/80 text-gray-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Ordered</th>
                <th className="text-left px-4 py-2">Buy this</th>
                <th className="text-left px-4 py-2">Send to</th>
                <th className="text-right px-4 py-2">Coins</th>
                <th className="text-right px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {data.redemptions.map((row) => (
                <tr key={row.id} className="hover:bg-gray-900/40">
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {new Date(row.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-gray-100 font-medium whitespace-nowrap">
                    {dollars(row.face_value_cents)} {row.brand_name}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {row.delivery_email}
                    {row.failure_reason && (
                      <div className="text-xs text-amber-400 mt-1">
                        Last attempt failed: {row.failure_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 whitespace-nowrap">
                    {row.coins_cost.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.status === 'pending_fulfillment' && (
                      <div className="flex gap-2 justify-end">
                        <button
                          disabled={busyId === row.id}
                          onClick={() => onFulfil(row)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-violet-500 bg-violet-950/40 text-violet-300 hover:bg-violet-900/40 disabled:opacity-50"
                        >
                          Fulfil
                        </button>
                        <button
                          disabled={busyId === row.id}
                          onClick={() => onRefund(row)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-700 bg-gray-900 text-gray-300 hover:text-gray-100 disabled:opacity-50"
                        >
                          Refund
                        </button>
                      </div>
                    )}
                    {row.status !== 'pending_fulfillment' && (
                      <span className="text-xs text-gray-500">{row.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
